import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Plus, Trash2, Save, ArrowLeft, BadgeEuro } from "lucide-react";
import { toast } from "sonner";
import { formatEuroIt } from "../lib/euro";
import { formatItInput, toItInputDisplay, formatNumIt } from "../lib/format";

const eur = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/\./g, "").replace(",", "."));
  return `${formatNumIt(Number.isFinite(n) ? n : 0)} €`;
};
const today = () => new Date().toISOString().slice(0, 10);
const TIPI = ["MATERIALE", "BIOLOGICO", "MISTO", "ALTRO"];
const MODALITA = ["ASSEGNO NUMERO", "BONIFICO", "CONTANTI", "ALTRO"];

function LField({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
const inputCls = "h-9 w-full bg-slate-900/70 border border-slate-700 rounded-md text-slate-100 px-3 text-sm";

export default function GestioneOffertaDialog({ open, onOpenChange, pratica }) {
  const [view, setView] = useState("list"); // list | detail
  const [offerte, setOfferte] = useState([]);
  const [cliente, setCliente] = useState("");
  const [quantTotale, setQuantTotale] = useState(0);
  const [form, setForm] = useState(null);
  const [impFocus, setImpFocus] = useState(false);
  const [impBuf, setImpBuf] = useState("");
  const [saving, setSaving] = useState(false);
  const pid = pratica?.id;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/studio/pratiche/${pid}/offerte`);
      setOfferte(data.offerte || []);
      setCliente(data.cliente || "");
      setQuantTotale(data.quantificazione_totale || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [pid]);

  useEffect(() => { if (open && pid) { setView("list"); load(); } }, [open, pid, load]);

  const openNew = () => {
    setForm({ data_ricevuta: "", data_accettazione: today(), tipo: "MATERIALE",
      offerta: quantTotale ? String(quantTotale) : "",
      modalita_pagamento: "ASSEGNO NUMERO", riferimento_pagamento: "", banca: "", inoltra_fax: "NO", trattenuta: false });
    setView("detail");
  };
  const openRow = (o) => { setForm({ ...o }); setView("detail"); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.id) await api.put(`/studio/pratiche/${pid}/offerte/${form.id}`, payload);
      else await api.post(`/studio/pratiche/${pid}/offerte`, payload);
      toast.success("Offerta salvata");
      await load();
      setView("list");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (o, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/studio/pratiche/${pid}/offerte/${o.id}`);
      toast.success("Offerta eliminata");
      setOfferte((x) => x.filter((i) => i.id !== o.id));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleTrattenuta = async (o, e) => {
    e.stopPropagation();
    try {
      await api.put(`/studio/pratiche/${pid}/offerte/${o.id}`, { trattenuta: !o.trattenuta });
      setOfferte((x) => x.map((i) => (i.id === o.id ? { ...i, trattenuta: !i.trattenuta } : i)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-stretch" data-testid="gestione-offerta-dialog">
      <div className="m-auto w-full h-full max-w-[1000px] bg-[#0a0d14] border border-slate-800 flex flex-col sm:my-4 sm:h-[calc(100%-2rem)] sm:rounded-xl overflow-hidden quant-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-[#0d111a]">
          <div className="flex items-center gap-2 min-w-0">
            <BadgeEuro className="h-5 w-5 text-amber-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-100">GESTIONE OFFERTA</div>
              <div className="text-[11px] text-slate-400">{view === "list" ? "DETTAGLIO OFFERTA" : (form?.id ? "MODIFICA OFFERTA" : "NUOVA OFFERTA")}</div>
            </div>
          </div>
          <button onClick={() => onOpenChange(false)} data-testid="offerta-close" className="text-slate-400 hover:text-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {view === "list" ? (
            <div>
              <div className="flex justify-end mb-3">
                <Button onClick={openNew} data-testid="offerta-add" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> Nuova offerta
                </Button>
              </div>
              <div className="rounded-lg border border-slate-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-400 text-xs uppercase bg-slate-900/50">
                    <th className="px-4 py-2.5">Totale</th><th className="px-4 py-2.5">Tipo</th><th className="px-4 py-2.5">Banca</th>
                    <th className="px-4 py-2.5">Accettata</th><th className="px-4 py-2.5">Trattenuta</th><th className="px-4 py-2.5 w-10" />
                  </tr></thead>
                  <tbody>
                    {offerte.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500" data-testid="offerta-empty">Nessuna offerta registrata</td></tr>
                    ) : offerte.map((o) => (
                      <tr key={o.id} onClick={() => openRow(o)} data-testid={`offerta-row-${o.id}`}
                        className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer transition-colors">
                        <td className="px-4 py-2.5 font-mono text-emerald-300">{eur(o.offerta)}</td>
                        <td className="px-4 py-2.5 text-slate-200">{o.tipo || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-300">{o.banca || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span data-testid={`offerta-accettata-${o.id}`} className={`text-xs font-semibold rounded px-2 py-0.5 ${o.data_accettazione ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700/50 text-slate-400"}`}>
                            {o.data_accettazione ? "Sì" : "No"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <button onClick={(e) => toggleTrattenuta(o, e)} data-testid={`offerta-trattenuta-${o.id}`}
                            className={`text-xs font-semibold rounded px-2 py-0.5 ${o.trattenuta ? "bg-amber-500/15 text-amber-300" : "bg-slate-700/50 text-slate-400"}`}>
                            {o.trattenuta ? "Sì" : "No"}
                          </button>
                        </td>
                        <td className="px-4 py-2.5">
                          <button onClick={(e) => del(o, e)} data-testid={`offerta-del-${o.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="max-w-2xl space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <LField label="Data offerta ricevuta">
                  <Input type="date" value={form.data_ricevuta || ""} onChange={(e) => setForm((f) => ({ ...f, data_ricevuta: e.target.value }))} data-testid="offerta-data-ricevuta" className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
                </LField>
                <LField label="Data accettazione offerta">
                  <Input type="date" value={form.data_accettazione || ""} onChange={(e) => setForm((f) => ({ ...f, data_accettazione: e.target.value }))} data-testid="offerta-data-accettazione" className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
                </LField>
              </div>
              <LField label="Cliente">
                <Input value={cliente} readOnly disabled data-testid="offerta-cliente" className="h-9 bg-slate-800/50 border-slate-700 text-slate-300" />
              </LField>
              <div className="grid sm:grid-cols-2 gap-4">
                <LField label="Tipo">
                  <select value={form.tipo || "MATERIALE"} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))} data-testid="offerta-tipo" className={inputCls}>
                    {TIPI.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </LField>
                <LField label="Offerta €">
                  <div className="relative"><Input value={impFocus ? impBuf : formatEuroIt(form.offerta)} onFocus={() => { setImpBuf(toItInputDisplay(form.offerta)); setImpFocus(true); }} onBlur={() => setImpFocus(false)} onChange={(e) => { const v = formatItInput(e.target.value); setImpBuf(v); setForm((f) => ({ ...f, offerta: v })); }} data-testid="offerta-importo" className="h-9 pr-7 text-right font-mono bg-slate-900/70 border-slate-700 text-slate-100" /><span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span></div>
                  {!form.id && quantTotale > 0 && <span className="block text-[10px] text-emerald-400 mt-1" data-testid="offerta-quant-hint">Precompilato dal totale Quantificazione: {eur(quantTotale)}</span>}
                </LField>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <LField label="Modalità pagamento">
                  <select value={form.modalita_pagamento || "ASSEGNO NUMERO"} onChange={(e) => setForm((f) => ({ ...f, modalita_pagamento: e.target.value }))} data-testid="offerta-modalita" className={inputCls}>
                    {MODALITA.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </LField>
                <LField label="Numero assegno / riferimento">
                  <Input value={form.riferimento_pagamento || ""} onChange={(e) => setForm((f) => ({ ...f, riferimento_pagamento: e.target.value }))} data-testid="offerta-riferimento" className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
                </LField>
              </div>
              <LField label="Nome banca">
                <Input value={form.banca || ""} onChange={(e) => setForm((f) => ({ ...f, banca: e.target.value }))} data-testid="offerta-banca" className="h-9 bg-slate-900/70 border-slate-700 text-slate-100" />
              </LField>
              <LField label="Accettata">
                <div className="flex gap-6 text-sm text-slate-200 h-9 items-center">
                  <label className="flex items-center gap-2"><input type="radio" checked={form.inoltra_fax === "SI"} onChange={() => setForm((f) => ({ ...f, inoltra_fax: "SI" }))} data-testid="offerta-fax-si" /> Sì</label>
                  <label className="flex items-center gap-2"><input type="radio" checked={form.inoltra_fax !== "SI"} onChange={() => setForm((f) => ({ ...f, inoltra_fax: "NO" }))} data-testid="offerta-fax-no" /> No</label>
                </div>
              </LField>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setView("list")} data-testid="offerta-indietro" className="border-slate-700 text-slate-200 hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-1" /> Indietro</Button>
                <Button onClick={save} disabled={saving} data-testid="offerta-salva" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Save className="h-4 w-4 mr-1" /> Salva</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
